import { useClose } from "@headlessui/react";
import { ReactNode } from "react";
import { HiChevronDown } from "react-icons/hi2";
import { ConfirmationModalClickWrapper } from "../layout/ConfirmationModalClickWrapper";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { Menu } from "./Menu";

type MoreActionsMenuProps = {
	items: (
		| {
				el: ReactNode;
				icon?: undefined;
				label?: undefined;
				onClick?: undefined;
				confirmationMessage?: undefined;
				className?: undefined;
		  }
		| {
				el?: undefined;
				icon?: React.ReactNode;
				label: string;
				onClick: () => void;
				confirmationMessage?: string;
				className?: string;
				disabled?: boolean;
				trackingId?: string;
		  }
		| false
		| undefined
	)[];
	className?: string;
	panelClassName?: string;
	collapsed?: boolean;
};

const MoreActionsMenuItem = ({
	item,
	onClick,
}: {
	item: MoreActionsMenuProps["items"][number];
	onClick?: () => void;
}) => {
	// If the element is false or undefined, render nothing
	if (!item) {
		return null;
	}

	if (item.label === undefined) {
		return item.el ?? null;
	}

	return (
		<div
			{...trackingAttrs(item.trackingId)}
			aria-disabled={item.disabled || undefined}
			className={cn(
				"flex items-center rounded-md space-x-[8px] px-[8px] py-[5.5px] text-[13px] font-medium text-gray-700",
				item.disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200/55 cursor-pointer",
			)}
			onClick={item.disabled ? undefined : onClick}
		>
			<span className="w-[16px] h-full">{item.icon && item.icon}</span>
			<span>{item.label}</span>
		</div>
	);
};

const MoreActionsMenuPanel = ({ items }: MoreActionsMenuProps) => {
	const close = useClose();

	return (
		<div className="flex flex-col w-full shrink-0 border space-y-[3px] border-gray-200 rounded-md cursor-pointer px-[3px] py-[5px] bg-surface-base">
			{items.map((item, index) => {
				if (!item) {
					return null;
				}

				if (item.confirmationMessage) {
					return (
						<ConfirmationModalClickWrapper
							key={`MA-${index}`}
							message={item.confirmationMessage}
							confirmTrackingId={item.trackingId ? `${item.trackingId}-confirm` : undefined}
							cancelTrackingId={item.trackingId ? `${item.trackingId}-cancel` : undefined}
							onConfirm={() => {
								item.onClick();
								close();
							}}
						>
							<MoreActionsMenuItem key={`MA-item-${index}`} item={item} onClick={() => {}} />
						</ConfirmationModalClickWrapper>
					);
				}
				return (
					<MoreActionsMenuItem
						key={`MA-item-${index}`}
						item={item}
						onClick={() => {
							item.onClick && item.onClick();
							close();
						}}
					/>
				);
			})}
		</div>
	);
};

export const MoreActionsMenu = ({
	items,
	className,
	panelClassName,
	collapsed,
}: MoreActionsMenuProps) => {
	return (
		<Menu
			testId="more-actions-button"
			collapsed={collapsed}
			button={
				<div
					className={cn(
						"flex items-center justify-center w-full h-fit space-x-[6px] text-[12px] leading-[12px] font-[475] text-secondary/90 ui-open:text-gray-800 hover:text-primary cursor:pointer",
						className,
					)}
				>
					<span>More Actions</span>
					<HiChevronDown className="stroke-[.5]" />
				</div>
			}
			buttonClassName="w-full justify-center"
			panelClassName={cn("shadow-sm shadow-gray-900/10 mt-[8px] w-[185px]", panelClassName)}
			anchor="bottom"
		>
			<MoreActionsMenuPanel items={items} />
		</Menu>
	);
};
